import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { shiftsService, volunteerApplicationService, bookingsService, withdrawalService, sickNoteService, authService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { NewsPost } from '../types/index';

const urlRegex = /(https?:\/\/[^\s]+)/g;

const renderTextWithLinks = (text: string) => {
  return text.split('\n').map((line, lineIndex) => {
    const parts = line.split(urlRegex);
    return (
      <React.Fragment key={lineIndex}>
        {parts.map((part, partIndex) => {
          if (/^https?:\/\/[^\s]+$/.test(part)) {
            return (
              <a
                key={`${lineIndex}-${partIndex}`}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {part}
              </a>
            );
          }
          return <React.Fragment key={`${lineIndex}-${partIndex}`}>{part}</React.Fragment>;
        })}
        {lineIndex < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    );
  });
};

export function VolunteerDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [applicationStatus, setApplicationStatus] = useState<'approved' | 'pending' | 'rejected' | 'waitlisted' | null>(null);
  const [bookingEnabled, setBookingEnabled] = useState<'no-access' | 'view-only' | 'booking-enabled'>('no-access');
  const [sickNoteEnabled, setSickNoteEnabled] = useState(false);
  const [userBookings, setUserBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState('Roskilde Festival');
  const [showShiftTitle, setShowShiftTitle] = useState(true);
  const [showShiftLocation, setShowShiftLocation] = useState(true);
  const [showNextShiftBox, setShowNextShiftBox] = useState(true);
  const [bookingDeadline, setBookingDeadline] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showBlockedWithdrawalModal, setShowBlockedWithdrawalModal] = useState(false);
  const [withdrawalPermitted, setWithdrawalPermitted] = useState(true);
  const [withdrawalDeadline, setWithdrawalDeadline] = useState<string | null>(null);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [withdrawalError, setWithdrawalError] = useState('');
  const [withdrawalSuccess, setWithdrawalSuccess] = useState('');
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [withdrawalRequests, setWithdrawalRequests] = useState<any[]>([]);
  const [pendingSickNotes, setPendingSickNotes] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [showWaitlistPosition, setShowWaitlistPosition] = useState(true);
  const [whatsappGroupJoinUrl, setWhatsappGroupJoinUrl] = useState('');
  const [whatsappGroupConfirmationCode, setWhatsappGroupConfirmationCode] = useState('');
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappModalReady, setWhatsappModalReady] = useState(false);
  const [whatsappConfirmationCode, setWhatsappConfirmationCode] = useState('');
  const [whatsappConfirmationError, setWhatsappConfirmationError] = useState('');
  const [whatsappConfirmationLoading, setWhatsappConfirmationLoading] = useState(false);
  const [waitlistOfferTimeLeft, setWaitlistOfferTimeLeft] = useState('');
  const [waitlistOfferExpiresAt, setWaitlistOfferExpiresAt] = useState<string | null>(null);
  const [waitlistOfferExists, setWaitlistOfferExists] = useState(false);
  const [waitlistOfferAccepted, setWaitlistOfferAccepted] = useState(false);
  const [waitlistOfferResponse, setWaitlistOfferResponse] = useState<string | null>(null);
  const [batchOfferId, setBatchOfferId] = useState<number | null>(null);
  const [withdrawingFromWaitlist, setWithdrawingFromWaitlist] = useState(false);
  const [newsPosts, setNewsPosts] = useState<NewsPost[]>([]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    setWhatsappModalReady(false);

    loadData();
  }, [authLoading, user, navigate]);

  // Countdown timer for waitlist offers
  useEffect(() => {
    if (!waitlistOfferExists || !waitlistOfferExpiresAt) {
      return;
    }
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiry = new Date(waitlistOfferExpiresAt).getTime();
      const distance = expiry - now;
      
      if (distance < 0) {
        setWaitlistOfferTimeLeft('Expired');
        return;
      }
      
      const hours = Math.floor(distance / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      setWaitlistOfferTimeLeft(`${hours}h ${minutes}m`);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [waitlistOfferExists, waitlistOfferExpiresAt]);

  const loadData = async () => {
    try {
      // Check application status
      const appRes = await volunteerApplicationService.getStatus();
      setApplicationStatus(appRes.data.status);

      // Get settings to check configuration
      const settingsRes = await shiftsService.getSettings();
      setBookingEnabled(settingsRes.data?.booking_enabled ?? 'no-access');
      setSickNoteEnabled(settingsRes.data?.sick_note_enabled ?? false);
      setWithdrawalPermitted(settingsRes.data?.withdrawal_permitted ?? true);
      setWithdrawalDeadline(settingsRes.data?.withdrawal_deadline ?? null);
      setEventName(settingsRes.data?.event_name || 'Roskilde Festival');
      setShowShiftTitle(settingsRes.data?.show_shift_title ?? true);
      setShowShiftLocation(settingsRes.data?.show_shift_location ?? true);
      setShowNextShiftBox(settingsRes.data?.show_next_shift_box ?? true);
      setShowWaitlistPosition(settingsRes.data?.show_waitlist_position ?? true);
      setBookingDeadline(settingsRes.data?.booking_deadline ?? null);
      setWhatsappGroupJoinUrl(settingsRes.data?.whatsapp_group_join_url ?? '');
      setWhatsappGroupConfirmationCode(settingsRes.data?.whatsapp_group_confirmation_code ?? '');
      const isActiveVolunteer = user?.role === 'volunteer' || user?.role === 'supervisor';
      setShowWhatsAppModal(!!user?.not_found_on_whatsapp && !!isActiveVolunteer && !!settingsRes.data?.whatsapp_group_join_url?.trim());
      setWhatsappModalReady(true);

      // Check if on waiting list
      if (appRes.data.status === 'waitlisted') {
        setLoading(false);
        // Store waiting list position and offer details
        (window as any).waitingListPosition = appRes.data.display_position || appRes.data.waiting_list_position;
        (window as any).waitlistOfferSentAt = appRes.data.waitlist_offer_sent_at;
        (window as any).waitlistOfferExpiresAt = appRes.data.waitlist_offer_expires_at;
        (window as any).waitlistOfferExists = appRes.data.waitlist_offer_sent_at && appRes.data.waitlist_offer_expires_at && new Date(appRes.data.waitlist_offer_expires_at) > new Date();
        
        // Set state for countdown timer
        const expiresAt = appRes.data.waitlist_offer_expires_at;
        const hasActiveOffer = appRes.data.waitlist_offer_sent_at && expiresAt && new Date(expiresAt) > new Date();
        setWaitlistOfferExpiresAt(expiresAt);
        setWaitlistOfferExists(hasActiveOffer);
        setWaitlistOfferAccepted(appRes.data.waitlist_offer_response === 'accepted');
        setWaitlistOfferResponse(appRes.data.waitlist_offer_response || null);
        setBatchOfferId(appRes.data.batch_offer_id ? Number(appRes.data.batch_offer_id) : null);
        return;
      }

      if (appRes.data.status !== 'approved') {
        setLoading(false);
        return;
      }

      // Get user bookings
      const bookingsRes = await bookingsService.getUserBookings();
      setUserBookings(bookingsRes.data || []);

      // Get withdrawal requests
      const withdrawalRes = await withdrawalService.getMine();
      const withdrawalList = withdrawalRes.data || [];
      setWithdrawalRequests(withdrawalList);
      const pendingWithdrawalCount = withdrawalList.filter((request: any) => request.status === 'pending').length;
      setPendingWithdrawals(pendingWithdrawalCount);

      // Get pending sick notes
      const sickNotesRes = await sickNoteService.getMine().catch(() => ({ data: { notes: [] } }));
      const pendingCount = (sickNotesRes.data?.notes || []).filter((note: any) => {
        const status = String(note.status || '').toLowerCase().trim();
        return status === 'pending' || status === 'doctor_required';
      }).length;
      setPendingSickNotes(pendingCount);

      const newsRes = await shiftsService.getNews().catch(() => ({ data: [] as NewsPost[] }));
      setNewsPosts(newsRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    setWhatsappConfirmationError('');

    if (!whatsappConfirmationCode.trim()) {
      setWhatsappConfirmationError('Please enter the confirmation code from the WhatsApp group description.');
      return;
    }

    setWhatsappConfirmationLoading(true);
    try {
      const res = await authService.confirmWhatsAppGroupJoin(whatsappConfirmationCode.trim());
      if (res.data?.user) {
        setShowWhatsAppModal(false);
        setWhatsappConfirmationCode('');
        window.location.reload();
      }
    } catch (err: any) {
      setWhatsappConfirmationError(err.response?.data?.error || 'Invalid confirmation code.');
    } finally {
      setWhatsappConfirmationLoading(false);
    }
  };

  // Countdown timer effect
  useEffect(() => {
    if (!bookingDeadline) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const deadline = new Date(bookingDeadline).getTime();
      const distance = deadline - now;

      if (distance < 0) {
        setCountdown(null);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [bookingDeadline]);

  if (loading) {
    return (
      <>
        <Header user={user} onLogout={logout} />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Loading...</div>
        </main>
      </>
    );
  }

  const handleSubmitWithdrawal = async () => {
    if (!withdrawalReason.trim()) {
      setWithdrawalError('Please provide a reason for your withdrawal.');
      return;
    }

    setSubmittingWithdrawal(true);
    setWithdrawalError('');

    try {
      await withdrawalService.submitWithdrawal(withdrawalReason);
      setWithdrawalSuccess('Your withdrawal request has been submitted successfully.');
      setShowWithdrawalModal(false);
      setWithdrawalReason('');
      
      // Refresh withdrawal requests
      const withdrawalRes = await withdrawalService.getMine();
      const withdrawalList = withdrawalRes.data || [];
      setWithdrawalRequests(withdrawalList);
      const pendingWithdrawalCount = withdrawalList.filter((request: any) => request.status === 'pending').length;
      setPendingWithdrawals(pendingWithdrawalCount);

      // Clear success message after 3 seconds
      setTimeout(() => setWithdrawalSuccess(''), 3000);
    } catch (err: any) {
      setWithdrawalError(err.response?.data?.error || 'Failed to submit withdrawal request.');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const handleWithdrawFromWaitlist = async () => {
    if (!confirm('Are you sure you want to withdraw your application from the waiting list? This action cannot be undone.')) {
      return;
    }

    setWithdrawingFromWaitlist(true);
    try {
      await volunteerApplicationService.withdrawFromWaitlist();
      window.location.reload();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to withdraw from waiting list.');
    } finally {
      setWithdrawingFromWaitlist(false);
    }
  };

  // Show withdrawal message if volunteer has been withdrawn
  if (user?.withdrawn) {
    return (
      <>
        <Header user={user} onLogout={logout} />
        {whatsappModalReady && showWhatsAppModal && whatsappGroupJoinUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-2xl border border-emerald-200 bg-white p-6 shadow-2xl relative">
              <button
                aria-label="Close modal"
                onClick={() => setShowWhatsAppModal(false)}
                className="absolute right-3 top-3 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <h2 className="text-2xl font-bold text-emerald-900">Join the WhatsApp group</h2>
              <p className="mt-3 text-sm text-gray-700">
                We could not find you on WhatsApp. Please join the volunteer group so we can share practical updates and coordination messages.
              </p>
              <p className="mt-3 text-sm text-gray-700">You cannot book shifts until you have joined the WhatsApp group.</p>
              <div className="mt-5">
                <a
                  href={whatsappGroupJoinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700"
                >
                  Join WhatsApp Group
                </a>
              </div>
            </div>
          </div>
        )}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-blue-100 border border-blue-400 text-blue-800 px-6 py-8 rounded-lg shadow-md">
            <h2 className="font-semibold text-2xl mb-4">You Have Withdrawn from {eventName}</h2>
            <p className="mb-4">
              Your withdrawal request has been approved. All your assigned shifts have been removed.
            </p>
            <p className="mb-4">
              We hope to see you next year at {eventName}!
            </p>
            <p className="text-sm">
              If you think this is a mistake, please email us at{' '}
              <a href="mailto:roskilde@connectingworlds.dk" className="underline font-semibold">
                roskilde@connectingworlds.dk
              </a>
            </p>
          </div>
        </main>
      </>
    );
  }

  if (applicationStatus !== 'approved') {
    // Show waiting list message if on waiting list
    if (applicationStatus === 'waitlisted') {
      const waitingPosition = (window as any).waitingListPosition || '?';
      const offerExpiresAt = (window as any).waitlistOfferExpiresAt;
      
      // Check if user has accepted and is awaiting admin confirmation
      if (waitlistOfferAccepted) {
        return (
          <>
            <Header user={user} onLogout={logout} />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="bg-purple-100 border border-purple-400 text-purple-900 px-6 py-4 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="text-4xl mr-4">✅</div>
                  <div className="flex-1">
                    <h2 className="font-bold text-xl mb-3">Your Spot is Secured!</h2>
                    <div className="space-y-3 text-purple-800">
                      <p className="text-lg">
                        <strong>Great news!</strong> You've accepted the offer and your volunteer spot is secured.
                      </p>
                      <div className="bg-purple-50 border-l-4 border-purple-500 p-4 my-4">
                        <p className="font-semibold text-base mb-2">
                          ⏳ Account Activation in Progress
                        </p>
                        <p className="text-sm">
                          Your volunteer account will be activated within the next few days. Don't worry - your spot is already reserved for you!
                        </p>
                      </div>
                      <p className="text-sm">
                        If you have any questions, please contact us at{' '}
                        <a href="mailto:roskilde@connectingworlds.dk" className="underline font-semibold">
                          roskilde@connectingworlds.dk
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </>
        );
      }
      
      // Check if there's a pending offer
      if ((window as any).waitlistOfferExists) {
        return (
          <>
            <Header user={user} onLogout={logout} />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="bg-green-100 border border-green-400 text-green-900 px-6 py-4 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="text-4xl mr-4">🎉</div>
                  <div className="flex-1">
                    <h2 className="font-bold text-xl mb-3">
                      A Spot Opened Up!
                      {batchOfferId && ' (Limited Selection)'}
                    </h2>
                    <div className="space-y-3 text-green-800">
                      <p className="text-lg">
                        <strong>Great news!</strong> {batchOfferId ? 'You\'ve been selected as one of multiple candidates for a volunteer spot! ' : ''}A volunteer spot {batchOfferId ? 'has become available' : 'became available'}, and we'd love to have you join us!
                      </p>
                      {batchOfferId && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                          <p className="font-semibold text-blue-900">
                            ⚡ Important: First to Accept Gets the Spot
                          </p>
                          <p className="text-sm text-blue-800 mt-2">
                            Multiple volunteers have received this offer. The first one to accept will become an approved volunteer. If someone beats you to it, you'll be automatically returned to the waiting list without needing to do anything.
                          </p>
                        </div>
                      )}
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 my-4">
                        <p className="font-semibold text-lg mb-2">
                          ⏰ Time to respond: <span className="text-2xl font-bold text-green-900">{waitlistOfferTimeLeft}</span>
                        </p>
                        <p className="text-sm text-green-700">
                          Offer expires: {new Date(offerExpiresAt).toLocaleString()}
                        </p>
                      </div>
                      <p>
                        <strong>What happens next?</strong>
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Accept to {batchOfferId ? 'compete for the spot' : 'become a volunteer and start booking shifts'}</li>
                        <li>Decline if you can no longer participate</li>
                        <li>If you don't respond in time, you'll be moved to the back of the waiting list</li>
                      </ul>
                      <div className="flex gap-4 mt-6 pt-4 border-t border-green-300">
                        <button
                          onClick={async () => {
                            try {
                              await volunteerApplicationService.acceptWaitlistOffer();
                              window.location.reload();
                            } catch (err: any) {
                              alert(err.response?.data?.error || 'Failed to accept offer');
                            }
                          }}
                          className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg"
                        >
                          ✓ Accept - I'm In!
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm('Are you sure you want to decline this offer? Your application will be withdrawn.')) {
                              try {
                                await volunteerApplicationService.declineWaitlistOffer();
                                window.location.reload();
                              } catch (err: any) {
                                alert(err.response?.data?.error || 'Failed to decline offer');
                              }
                            }
                          }}
                          className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-lg"
                        >
                          ✗ Decline
                        </button>
                      </div>
                      <div className="mt-3">
                        <button
                          onClick={handleWithdrawFromWaitlist}
                          disabled={withdrawingFromWaitlist}
                          className="w-full px-6 py-3 bg-white text-red-700 border border-red-400 rounded-lg hover:bg-red-50 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {withdrawingFromWaitlist ? 'Withdrawing...' : 'Withdraw from Waiting List'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </>
        );
      }
      
      // Check if offer was rejected due to batch being filled
      if (waitlistOfferResponse === 'rejected_batch_filled') {
        return (
          <>
            <Header user={user} onLogout={logout} />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-900 px-6 py-4 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="text-4xl mr-4">⚡</div>
                  <div className="flex-1">
                    <h2 className="font-bold text-xl mb-3">Spot Filled by Another Volunteer</h2>
                    <div className="space-y-2 text-yellow-800">
                      <p>
                        Unfortunately, another volunteer accepted the offer before you. You've been automatically returned to the waiting list.
                      </p>
                      <p className="mt-3 pt-3 border-t border-yellow-300">
                        <strong>Good news:</strong> You're now back in the waiting list and will be considered for future opportunities. We'll contact you again if another spot opens up!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <div className="bg-blue-100 border border-blue-400 text-blue-900 px-6 py-4 rounded-lg shadow-md">
                  <div className="flex items-start">
                    <div className="text-4xl mr-4">⏳</div>
                    <div className="flex-1">
                      <h2 className="font-bold text-xl mb-3">You're on the Waiting List</h2>
                      <div className="space-y-2 text-blue-800">
                        <p>
                          Thank you for your application! We've received more applications than spots available,
                          so we've placed you on our waiting list.
                        </p>
                        {showWaitlistPosition && (
                          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                            <p className="font-semibold text-lg">
                              Your Position: <span className="text-2xl font-bold text-blue-900">#{waitingPosition}</span>
                            </p>
                          </div>
                        )}
                        <p>
                          <strong>What happens next?</strong>
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>If a spot opens up, you'll be notified by email with an offer</li>
                          <li>Offers are sent to multiple volunteers at once, and the first person to accept gets the spot</li>
                          <li>You'll have a limited time (usually 48 hours) to accept or decline the offer</li>
                          <li>If you accept first, you'll become a volunteer and can start booking shifts</li>
                          <li>If you decline, your application will be withdrawn</li>
                          <li>If you don't respond in time, you'll be moved to the back of the waiting list</li>
                          <li>You'll move up as volunteers ahead of you decline or drop out</li>
                        </ul>
                        <p className="mt-4 pt-4 border-t border-blue-300">
                          Questions? Contact us at{' '}
                          <a href="mailto:roskilde@connectingworlds.dk" className="underline font-semibold hover:text-blue-950">
                            roskilde@connectingworlds.dk
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleWithdrawFromWaitlist}
                    disabled={withdrawingFromWaitlist}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {withdrawingFromWaitlist ? 'Withdrawing...' : 'Withdraw from Waiting List'}
                  </button>
                </div>
              </div>
            </main>
          </>
        );
      }
      
      // No pending offer - show regular waiting list message
      return (
        <>
          <Header user={user} onLogout={logout} />
          <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-blue-100 border border-blue-400 text-blue-900 px-6 py-4 rounded-lg shadow-md">
              <div className="flex items-start">
                <div className="text-4xl mr-4">⏳</div>
                <div className="flex-1">
                  <h2 className="font-bold text-xl mb-3">You're on the Waiting List</h2>
                  <div className="space-y-2 text-blue-800">
                    <p>
                      Thank you for your application! We've received more applications than spots available,
                      so we've placed you on our waiting list.
                    </p>
                    {showWaitlistPosition && (
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                        <p className="font-semibold text-lg">
                          Your Position: <span className="text-2xl font-bold text-blue-900">#{waitingPosition}</span>
                        </p>
                      </div>
                    )}
                    <p>
                      <strong>What happens next?</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>If a spot opens up, you'll be notified by email with an offer</li>
                      <li>Offers are sent to multiple volunteers at once, and the first person to accept gets the spot</li>
                      <li>You'll have a limited time (usually 48 hours) to accept or decline the offer</li>
                      <li>If you accept first, you'll become a volunteer and can start booking shifts</li>
                      <li>If you decline, your application will be withdrawn</li>
                      <li>If you don't respond in time, you'll be moved to the back of the waiting list</li>
                      <li>You'll move up as volunteers ahead of you decline or drop out</li>
                    </ul>
                    <p className="mt-4 pt-4 border-t border-blue-300">
                      Questions? Contact us at{' '}
                      <a href="mailto:roskilde@connectingworlds.dk" className="underline font-semibold hover:text-blue-950">
                        roskilde@connectingworlds.dk
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-center">
              <button
                onClick={handleWithdrawFromWaitlist}
                disabled={withdrawingFromWaitlist}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {withdrawingFromWaitlist ? 'Withdrawing...' : 'Withdraw from Waiting List'}
              </button>
            </div>
          </main>
        </>
      );
    }
    
    // Show rejection message if application was rejected
    if (applicationStatus === 'rejected') {
      return (
        <>
          <Header user={user} onLogout={logout} />
          <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <h2 className="font-semibold text-lg mb-2">Application Not Approved</h2>
              <p>Unfortunately, your volunteer application has not been approved. If you have questions, please contact us at{' '}
                <a href="mailto:roskilde@connectingworlds.dk" className="underline font-medium">
                  roskilde@connectingworlds.dk
                </a>
              </p>  
            </div>
          </main>
        </>
      );
    }
    
    // Redirect to signup for null or pending status
    navigate('/signup');
    return null;
  }

  const now = new Date();
  const currentBookedShifts = userBookings
    .filter((booking) => new Date(booking.start_time) <= now && new Date(booking.end_time) >= now)
    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  const nextBookedShifts = userBookings
    .filter((booking) => new Date(booking.start_time) > now)
    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  const featuredBookedShift = currentBookedShifts[0] || nextBookedShifts[0] || null;
  const featuredShiftLabel = currentBookedShifts.length > 0 ? 'Current shift' : 'Next shift';

  const formatBookedShiftDate = (value: string) => {
    const date = new Date(value);
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  // Determine current step
  const currentStep = bookingEnabled === 'booking-enabled'
    ? (userBookings.length > 0 ? 3 : 2)
    : 1;

  const steps = [
    {
      number: 1,
      title: 'Registration as Volunteer',
      description: '✓ Confirmed and approved',
      icon: (
        <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ),
      bgColor: 'bg-green-500',
    },
    {
      number: 2,
      title: 'Choose Your Shifts',
      description: bookingEnabled === 'booking-enabled'
        ? (userBookings.length > 0 
            ? `✓ ${userBookings.length} shift${userBookings.length !== 1 ? 's' : ''} booked`
            : 'Book your shifts now')
        : bookingEnabled === 'view-only'
        ? 'View available shifts (booking disabled)'
        : 'Shift access not yet available',
      icon: userBookings.length > 0 ? (
        <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3M3 12h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
</svg>
      ),
      bgColor: bookingEnabled === 'booking-enabled' ? 'bg-blue-500' : 'bg-gray-400',
    },
    {
      number: 3,
      title: 'Roskilde Festival',
      description: '🎉 Get ready for an amazing experience! Visit our FAQ for all info to get prepared.',
      icon: (
        <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM15.657 14.243a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM11 17a1 1 0 102 0v-1a1 1 0 10-2 0v1zM5.757 15.657a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM2 10a1 1 0 011-1h1a1 1 0 110 2H3a1 1 0 01-1-1zM5.757 4.343a1 1 0 00-1.414 1.414l.707.707a1 1 0 001.414-1.414l-.707-.707z" />
        </svg>
      ),
      bgColor: userBookings.length > 0 ? 'bg-orange-500' : 'bg-gray-400',
    },
  ];

  const whatsappModal = whatsappModalReady && showWhatsAppModal && whatsappGroupJoinUrl && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-emerald-200 bg-white p-6 shadow-2xl relative">
        <button
          aria-label="Close modal"
          onClick={() => setShowWhatsAppModal(false)}
          className="absolute right-3 top-3 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        <h2 className="text-2xl font-bold text-emerald-900">Join the WhatsApp group</h2>
        <p className="mt-3 text-sm text-gray-700">
          We could not find you on WhatsApp. Please join the volunteer group so we can share practical updates and coordination messages.
        </p>
        <p className="mt-3 text-sm text-gray-700">You cannot book shifts until you have joined the WhatsApp group.</p>
        <div className="mt-5">
          <a
            href={whatsappGroupJoinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700"
          >
            Join WhatsApp Group
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Header user={user} onLogout={logout} hasBookings={userBookings.length > 0} sickNoteEnabled={sickNoteEnabled} isLoadingBookings={loading} />
      {whatsappModal}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content - 3 columns */}
          <div className="lg:col-span-3 space-y-8">
            {/* Header */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Your Festival Journey</h1>
              <p className="text-gray-600">Welcome! Follow these steps to get ready for Roskilde Festival.</p>
            </div>

            {showNextShiftBox && userBookings.length > 0 && featuredBookedShift && (
              <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 sm:p-5 shadow-sm">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-800">
                    {featuredShiftLabel}
                  </span>
                  <p className="text-sm font-medium text-blue-900">Please arrive 15 minutes early.</p>
                </div>

                <div className="rounded-xl border border-blue-100 bg-white p-4 sm:p-5">
                  <div className="space-y-2">
                    {showShiftTitle && (
                      <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 leading-tight">
                        {featuredBookedShift.title || 'Shift'}
                      </h2>
                    )}
                    <p className="text-lg sm:text-xl font-semibold text-blue-950 leading-tight">
                      {formatBookedShiftDate(featuredBookedShift.start_time)}
                    </p>
                    <p className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">
                      {featuredBookedShift.start_time?.substring(11, 16)} - {featuredBookedShift.end_time?.substring(11, 16)}
                    </p>
                    {showShiftLocation && featuredBookedShift.location && (
                      <a
                        href="https://homebase.connectingworlds.dk"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-sm text-blue-700 hover:underline"
                      >
                        📍 {featuredBookedShift.location}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {pendingSickNotes > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-yellow-900 font-medium">Pending sick note request{pendingSickNotes > 1 ? 's' : ''}</p>
                    <p className="text-sm text-yellow-800 mt-1">
                      You have {pendingSickNotes} sick note request{pendingSickNotes > 1 ? 's' : ''} pending review. We’ll notify you when it’s processed.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/sick-note')}
                    className="px-3 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm font-medium"
                  >
                    View sick note
                  </button>
                </div>
              </div>
            )}

            {pendingWithdrawals > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-red-900 font-medium">Pending withdrawal request</p>
                    <p className="text-sm text-red-800 mt-1">
                      You have requested to withdraw from the festival. Your request is pending review. We'll notify you when it's processed.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Booking Deadline Countdown */}
            {countdown && bookingEnabled === 'booking-enabled' && userBookings.length === 0 && (
              <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-6 text-center">
                <h3 className="text-lg font-semibold text-orange-900 mb-2">⏰ Shift Booking Deadline</h3>
                <p className="text-sm text-orange-800 mb-4">Complete your shift bookings before:</p>
                <div className="flex justify-center gap-4 mb-2">
                  <div className="bg-white rounded-lg p-3 min-w-[70px]">
                    <div className="text-3xl font-bold text-orange-600">{countdown.days}</div>
                    <div className="text-xs text-gray-600 uppercase">Days</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 min-w-[70px]">
                    <div className="text-3xl font-bold text-orange-600">{countdown.hours}</div>
                    <div className="text-xs text-gray-600 uppercase">Hours</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 min-w-[70px]">
                    <div className="text-3xl font-bold text-orange-600">{countdown.minutes}</div>
                    <div className="text-xs text-gray-600 uppercase">Minutes</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 min-w-[70px]">
                    <div className="text-3xl font-bold text-orange-600">{countdown.seconds}</div>
                    <div className="text-xs text-gray-600 uppercase">Seconds</div>
                  </div>
                </div>
                <p className="text-xs text-orange-700">
                  {bookingDeadline?.replace('T', ' ')}
                </p>
              </div>
            )}

            {/* Progress Stepper */}
            <div className="relative">
              {/* Vertical Progress Line */}
              <div className="absolute left-6 top-16 bottom-0 w-1 bg-gray-200"></div>

              {/* Steps */}
              <div className="space-y-8">
                {steps.map((step, index) => {
                  const isCompleted = index < currentStep - 1;
                  const isCurrent = index === currentStep - 1;

                  return (
                    <div key={step.number}>
                      <div className="relative flex gap-6">
                        {/* Step Circle */}
                        <div className={`flex items-center justify-center h-12 w-12 rounded-full relative z-10 flex-shrink-0 ${
                          isCompleted 
                            ? 'bg-green-500' 
                            : isCurrent 
                              ? `${step.bgColor} ring-4 ring-blue-200 shadow-lg`
                              : index === currentStep ? step.bgColor : 'bg-gray-300'
                        }`}>
                          {step.icon}
                        </div>

                        {/* Step Content */}
                        <div className="pt-0 pb-6 flex-1">
                        <h3 className={`text-lg font-semibold ${isCurrent ? 'text-blue-600' : 'text-gray-900'}`}>
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{step.description}</p>

                        {/* Withdrawal for Step 1 */}
                        {step.number === 1 && (
                          <div className="mt-3">
                            {withdrawalRequests.some(w => w.status === 'pending') ? (
                              <p className="text-sm text-orange-600 font-medium">You have a pending withdrawal request</p>
                            ) : (
                              <button
                                onClick={() => {
                                if (!withdrawalPermitted) {
                                  setShowBlockedWithdrawalModal(true);
                                } else if (userBookings.length > 0) {
                                  setShowBlockedWithdrawalModal(true);
                                } else {
                                  setShowWithdrawalModal(true);
                                }
                              }}
                                className="text-sm text-red-600 hover:text-red-700 font-medium"
                              >
                                Request withdrawal from festival →
                              </button>
                            )}
                          </div>
                        )}

                        {/* Step-specific Actions */}
                        {step.number === 2 && bookingEnabled !== 'no-access' && (
                          <button
                            onClick={() => navigate('/shifts')}
                            className="inline-flex items-center mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                          >
                            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 3a2 2 0 012-2h6a2 2 0 012 2v2H5V3zM4 6h12v8a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                            </svg>
                            {bookingEnabled === 'view-only' ? 'View Shifts' : (userBookings.length > 0 ? 'View Shifts' : 'Book Now')}
                          </button>
                        )}
                        {step.number === 2 && userBookings.length > 0 && (
                          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-2">Booked Shifts</h4>
                            <div className="space-y-2">
                              {userBookings
                                .slice()
                                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                                .map((booking) => {
                                const startStr = booking.start_time.substring(0, 16).replace('T', ' ');
                                const endStr = booking.end_time.substring(0, 16).replace('T', ' ');
                                const startDate = startStr.split(' ')[0];
                                const endDate = endStr.split(' ')[0];
                                const [year, month, day] = startDate.split('-').map(Number);
                                const formattedDate = new Intl.DateTimeFormat('en-GB', {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                }).format(new Date(Date.UTC(year, month - 1, day)));
                                const startTime = startStr.split(' ')[1];
                                const endTime = endStr.split(' ')[1];
                                const nextDaySuffix = startDate !== endDate ? ' (next day)' : '';

                                return (
                                  <div key={booking.id || booking.booking_id} className="text-sm text-gray-700">
                                    <span className="text-green-600 mr-2">✓</span>
                                    {showShiftTitle && (
                                      <span className="font-medium">{booking.title || 'Shift'}</span>
                                    )}
                                    <span className="text-gray-600">
                                      {showShiftTitle ? ' · ' : ''}
                                      <span className="text-gray-900">{formattedDate}</span>
                                      {' '}{startTime} - {endTime}{nextDaySuffix}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {step.number === 3 && (
                          <div className="mt-3">
                            <a
                              href="https://faq.connectingworlds.dk"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium text-sm"
                            >
                            Visit FAQ for preparation info
                            <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                          {sickNoteEnabled && (
                            <p className="text-sm text-gray-600 mt-3">
                              If you get sick, you can report it using the{' '}
                              <Link to="/sick-note" className="text-purple-600 hover:text-purple-700 font-medium">
                                Sick Note
                              </Link>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          </div>

          {/* Sidebar - 1 column */}
          <aside className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Upcoming Deadlines</h2>
              <div className="space-y-6">
                {/* Withdrawal Deadline */}
                {withdrawalDeadline && (
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 text-center">
                      <div className="bg-red-100 rounded border-b-4 border-red-300 p-3 min-w-fit">
                        <p className="text-3xl font-bold text-red-600">{new Date(withdrawalDeadline).getDate().toString().padStart(2, '0')}</p>
                        <p className="text-xs font-semibold text-gray-700 uppercase mt-1">{new Date(withdrawalDeadline).toLocaleDateString('en-GB', { month: 'short' })}</p>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-sm font-medium text-gray-900">Withdrawal Request<br/>Deadline</p>
                    </div>
                  </div>
                )}
                
                {/* Event 1 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 text-center">
                    <div className="bg-blue-100 rounded border-b-4 border-blue-300 p-3 min-w-fit">
                      <p className="text-3xl font-bold text-blue-600">01</p>
                      <p className="text-xs font-semibold text-gray-700 uppercase mt-1">May</p>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center">
                    <p className="text-sm font-medium text-gray-900">Volunteer Camping<br/>Booking Opens</p>
                  </div>
                </div>

                {/* Event 2 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 text-center">
                    <div className="bg-green-100 rounded border-b-4 border-green-300 p-3 min-w-fit">
                      <p className="text-3xl font-bold text-green-600">21</p>
                      <p className="text-xs font-semibold text-gray-700 uppercase mt-1">June</p>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center">
                    <p className="text-sm font-medium text-gray-900">Wristband Pickup<br/>Opens</p>
                  </div>
                </div>

                {/* Event 3 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 text-center">
                    <div className="bg-orange-100 rounded border-b-4 border-orange-300 p-3 min-w-fit">
                      <p className="text-3xl font-bold text-orange-600">27</p>
                      <p className="text-xs font-semibold text-gray-700 uppercase mt-1">June</p>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center">
                    <p className="text-sm font-medium text-gray-900">Roskilde Festival<br/>Begins</p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-10 bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">News & Updates</h2>
          {newsPosts.length === 0 ? (
            <p className="text-sm text-gray-600">No updates yet.</p>
          ) : (
            <div className="space-y-4">
              {newsPosts.map((post) => (
                <article key={post.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900">{post.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(post.created_at).toLocaleString('en-GB')}
                  </p>
                  <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">{renderTextWithLinks(post.content)}</p>
                </article>
              ))}
            </div>
          )}
        </section>

        <div className="mt-8 text-sm text-gray-600">
          Questions? Email us at{' '}
          <a href="mailto:roskilde@connectingworlds.dk" className="text-blue-600 hover:underline">
            roskilde@connectingworlds.dk
          </a>.
        </div>
      </main>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Request Withdrawal from Festival</h3>
            {withdrawalError && (
              <div className="bg-red-100 text-red-800 px-4 py-3 rounded mb-4">{withdrawalError}</div>
            )}
            <p className="text-sm text-gray-700 mb-4">
              {withdrawalDeadline ? (
                <>
                  Please note: Withdrawal requests must be submitted by{' '}
                  <strong>{new Date(withdrawalDeadline).toLocaleDateString('en-GB', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                </>
              ) : (
                'Please provide a reason for your withdrawal request below.'
              )}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for withdrawal *
              </label>
              <textarea
                value={withdrawalReason}
                onChange={(e) => setWithdrawalReason(e.target.value)}
                className="w-full border rounded px-3 py-2 h-32"
                placeholder="Please provide a brief explanation..."
                maxLength={2000}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{withdrawalReason.length}/2000 characters</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowWithdrawalModal(false);
                  setWithdrawalReason('');
                  setWithdrawalError('');
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                disabled={submittingWithdrawal}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitWithdrawal}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                disabled={submittingWithdrawal || !withdrawalReason.trim()}
              >
                {submittingWithdrawal ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blocked Withdrawal Modal */}
      {showBlockedWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {!withdrawalPermitted ? 'Withdrawals Are No Longer Possible' : 'Withdrawal Not Possible'}
            </h3>
            {!withdrawalPermitted ? (
              <p className="text-sm text-gray-700 mb-4">
                Withdrawals are no longer possible.
              </p>
            ) : (
              <p className="text-sm text-gray-700 mb-4">
                You have booked shifts and cannot submit a withdrawal request. The only option is to find someone to take over your shifts.{' '}
                Please email us at <a href="mailto:roskilde@connectingworlds.dk" className="text-blue-600 hover:underline">roskilde@connectingworlds.dk</a> with 
                the details of the person. Only after they are confirmed signed up, we can withdraw you.
              </p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => setShowBlockedWithdrawalModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {withdrawalSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-100 text-green-800 px-6 py-3 rounded shadow-lg">
          {withdrawalSuccess}
        </div>
      )}
    </>
  );
}
